import { useMotionDisabled } from "@gladis/ui/motion";
import { motion, type Transition } from "motion/react";
import type { ReactNode } from "react";

/**
 * The layout every entry screen renders into — sign-in, the token handoff, an invite, onboarding.
 *
 * One component rather than the same three classes repeated on five pages, because the reveal has
 * to be identical everywhere: these screens hand off to each other, and a fade that differs by a
 * tenth of a second between two steps of one flow reads as a glitch.
 *
 * The app's own pages — dashboard, settings — deliberately do NOT use this. They have headers and
 * lists that grow, and vertical centring fights content whose height is not known in advance.
 */

/** Far enough to read as movement, near enough that nothing appears to fly in. */
const REVEAL_DISTANCE = 16;

/**
 * `easeOutCubic`. An ease-out, so the content decelerates into place rather than stopping dead —
 * but a gentle one. The first curve tried here was `easeOutExpo`, and measuring it showed the fade
 * was 96% finished 60ms into a 450ms animation: technically an animation, perceptually a pop.
 */
const REVEAL: Transition = {
  duration: 0.5,
  ease: [0.33, 1, 0.68, 1],
};

export function AuthPage({ children }: { children: ReactNode }): ReactNode {
  /*
   * The same switch the background reads, so a still gradient and content that simply appears
   * always travel together.
   *
   * `initial={false}` rather than a zero-duration animation: it tells motion to mount at the final
   * values and skip the enter animation entirely, so there is no first frame at opacity 0.
   */
  const motionDisabled = useMotionDisabled();

  return (
    <main className="auth-shell">
      <motion.div
        className="auth-panel"
        initial={motionDisabled ? false : { opacity: 0, y: REVEAL_DISTANCE }}
        animate={{ opacity: 1, y: 0 }}
        transition={REVEAL}
      >
        {children}
      </motion.div>
    </main>
  );
}
